import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Stack, TextField, Tooltip } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { useDispatch, useSelector } from 'react-redux';
import DomainSelector from './domain-selector';
import { setFromSearch, clearPrependContext } from 'store/conciergeSlice';
import { merge } from 'lodash';
import { ICustomStyles } from 'app/concierge/panel';
import { ChatId } from 'app/concierge/hooks';
import defaultStyles from './styles';

export default function Footer({
    activeChat,
    setActiveChat,
    setActiveChatRequest,
    startNewChat,
    onAbort,
    loading,
    loadingToSendNewRequestMessage,
    showSourceSelector,
    displayModelName,
    autoFocus = true,
}: {
    activeChat: ChatId;
    setActiveChat: (id: number) => void;
    setActiveChatRequest: (request: string) => void;
    startNewChat: () => void;
    onAbort: () => void;
    loading: boolean;
    loadingToSendNewRequestMessage: boolean;
    showSourceSelector?: boolean;
    displayModelName?: boolean;
    autoFocus?: boolean;
}) {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const [requestVal, setRequestVal] = useState<string>('');
    const isWelcome = !activeChat;
    const dispatch = useDispatch();
    const [menuOpened, setMenuOpened] = useState<boolean>(false);

    const conciergeSettings = useSelector((state: any) => state.auth?.configuration?.conciergeSettings) ?? {};
    const testSession = useSelector((state: any) => state.concierge.testSession);
    const showLLM =
        displayModelName !== undefined ? displayModelName : conciergeSettings.show_llm_model_in_concierge_ind == 'Y';
    const llmModel = conciergeSettings.llm_model ?? '';
    const showSources = testSession
        ? false
        : showSourceSelector !== undefined
          ? showSourceSelector
          : conciergeSettings.show_all_sources_ind == 'Y';

    const visible = useSelector((state: any) => state.concierge.visible);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const focusInputTrigger = useSelector((state: any) => state.concierge.focusInput);
    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;

    const styles = useMemo(() => {
        return merge({}, defaultStyles, { container: customStyles?.footer }) as typeof defaultStyles;
    }, [customStyles]);

    const isInsidePanel = (el: Element | null) => {
        if (!el) return false;
        return !!containerRef.current && containerRef.current.contains(el);
    };

    const focusInput = () => {
        if (!autoFocus) return;
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };

    useEffect(() => {
        if (visible) {
            focusInput();
        }
    }, [visible]);

    useEffect(() => {
        const active = typeof document !== 'undefined' ? (document.activeElement as Element | null) : null;
        if (isInsidePanel(active)) {
            focusInput();
        }
    }, [focusInputTrigger]);

    const onSubmit = () => {
        if (requestVal == '') return false;
        setMenuOpened(false);
        setTimeout(() => {
            if (isWelcome) {
                setActiveChat(Date.now());
                setActiveChatRequest(requestVal);
            } else {
                setActiveChatRequest(requestVal);
            }
            setRequestVal('');
            dispatch(setFromSearch(false));
        }, 0);
    };

    const onSelectDomainPrompt = useCallback((prompt: string) => {
        setActiveChat(Date.now());
        setActiveChatRequest(prompt);
        setRequestVal('');
        dispatch(setFromSearch(false));
    }, []);

    useEffect(() => {
        if (!loading && !loadingToSendNewRequestMessage && requestVal === '') {
            const active = typeof document !== 'undefined' ? (document.activeElement as Element | null) : null;
            if (!active || active === document.body || isInsidePanel(active)) {
                focusInput();
            }
        }
    }, [loading, loadingToSendNewRequestMessage, requestVal]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            onSubmit();
        }
    };

    const sendButton = (
        <Box sx={styles.sendButtonHolder}>
            <Box
                flexShrink={0}
                className={!loading && !loadingToSendNewRequestMessage && requestVal != '' ? 'active' : undefined}
                sx={styles.sendBtn}
                onClick={onSubmit}
            >
                {loading || loadingToSendNewRequestMessage ? (
                    <CircularProgress size={'16px'} />
                ) : (
                    <IconMi icon={'send'} sx={{ fontSize: '16px' }} />
                )}
            </Box>
            {showLLM && (
                <Tooltip title={`${t('footer.llm_model')}: ${llmModel}`}>
                    <Box sx={styles.llmModelHolder}>
                        <IconMi icon={'info-circle'} sx={{ fontSize: '12px' }} />
                    </Box>
                </Tooltip>
            )}
        </Box>
    );

    const abortButton = loading && (
        <Box sx={styles.abortHolder} onClick={onAbort}>
            <IconMi icon={'pause'} sx={{ fontSize: '16px' }} />
        </Box>
    );

    const inputField = (
        <Box flexGrow={1} sx={styles.fieldHolder} className={isWelcome ? 'is-welcome' : undefined}>
            <TextField
                inputRef={inputRef}
                sx={styles.field}
                value={requestVal}
                onChange={(event) => {
                    setRequestVal(event.target.value);
                }}
                minRows={1}
                maxRows={3}
                fullWidth
                placeholder={t('footer.field_placeholder')}
                multiline={true}
                disabled={loading || loadingToSendNewRequestMessage}
                onKeyDown={handleKeyDown}
                InputProps={{
                    classes: {
                        input: 'alwaysShowPlaceholder',
                    },
                }}
            />
        </Box>
    );

    return (
        <Stack sx={styles.container} ref={containerRef}>
            {showSources ? (
                <Stack>
                    <Box sx={styles.sourcesInputRow}>{inputField}</Box>
                    <Stack direction={'row'} justifyContent={'space-between'} sx={styles.sourcesRow}>
                        <DomainSelector
                            loading={loading || loadingToSendNewRequestMessage}
                            onSelectDomainPrompt={onSelectDomainPrompt}
                            menuOpened={menuOpened}
                            setMenuOpened={setMenuOpened}
                        />
                        <Stack direction={'row'} alignItems={'center'}>
                            {abortButton}
                            {sendButton}
                        </Stack>
                    </Stack>
                </Stack>
            ) : (
                <Stack direction={'row'} sx={styles.fieldContainer}>
                    {inputField}
                    {abortButton}
                    {sendButton}
                </Stack>
            )}
            {!isWelcome && !testSession && (
                <Stack
                    direction={'row'}
                    sx={styles.newChatBtn}
                    className={loading || loadingToSendNewRequestMessage ? 'disabled' : undefined}
                    onClick={() => {
                        if (loading || loadingToSendNewRequestMessage) {
                            return;
                        }
                        setRequestVal('');
                        dispatch(clearPrependContext());
                        startNewChat();
                        focusInput();
                    }}
                >
                    <IconMi icon="new" sx={{ fontSize: '16px', mr: 0.5 }} />
                    <Box>{t('footer.new_chat_btn')}</Box>
                </Stack>
            )}
        </Stack>
    );
}
